import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getContractTypeLabel } from '../../constants/labels/user';
import { useFormResponseDocumentsQuery } from '../../hooks/queries/qhse/forms/useFormResponseDocumentsQuery';
import { useResponseByIdQuery } from '../../hooks/queries/qhse/forms/useFormResponsesQuery';
import { useFormAttendedUsersQuery } from '../../hooks/queries/qhse/forms/useFormResponseUsersQuery';
import { useBoolean } from '../../hooks/useBoolean';
import { FormTemplateWithSections } from '../../services/apiSLTP/qhse/forms/formTemplates';
import { formatName } from '../../utils/capitalize';
import PDFModal from '../modals/PDFModal';
import FormResponsePDF from '../pdf/FormResponsePDF';
import Icon from '../ui/Icon';
import IconButton from '../ui/IconButton';
import SectionResponseItem from './responses/SectionResponseItem';

interface FormResponseProps {
    formTemplate: FormTemplateWithSections;
    selectedResponseId: string;
    onBack: () => void;
}

const FormResponse: React.FC<FormResponseProps> = ({ formTemplate, selectedResponseId, onBack }) => {
    const { t } = useTranslation();
    const pdfViewerModal = useBoolean(false);

    const { data: dataResponse, isLoading: isResponseLoading, refetch: refetchResponse } = useResponseByIdQuery({ id: selectedResponseId });
    const {
        data: dataDocuments,
        isLoading: isLoadingDocuments,
        refetch: refetchDocuments,
    } = useFormResponseDocumentsQuery({ responseId: selectedResponseId });
    const {
        data: dataAttendedUsers,
        isLoading: isLoadingAttendedUsers,
        refetch: refetchAttendedUsers,
    } = useFormAttendedUsersQuery({ responseId: selectedResponseId });

    return (
        <Box sx={{ padding: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'row', gap: 2 }}>
                    <IconButton iconName="arrowBack" onClick={onBack} />
                    <Typography variant="h4">{formatName(dataResponse?.filledBy.firstName, dataResponse?.filledBy.lastName)}</Typography>
                    <Typography variant="h6">{dataResponse?.submittedAt ? new Date(dataResponse?.submittedAt).toLocaleString() : ''}</Typography>
                </Box>
                <Box>
                    <IconButton iconName="print" onClick={pdfViewerModal.setTrue} />
                </Box>
            </Box>

            {formTemplate.isSiteEnabled && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 2 }}>
                    <Typography variant="subtitle1">{dataResponse?.siteId?.name}</Typography>
                </Box>
            )}

            {(dataResponse?.attendees.length || 0) > 0 && (
                <Stack sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                    {dataResponse?.attendees.map((attendee) => {
                        const isValidated = attendee.hasValidatedPresence;
                        const validationDate = attendee.validatedAt ? new Date(attendee.validatedAt).toLocaleString('fr-FR') : null;
                        const contractTypeLabel = getContractTypeLabel(attendee.contractType).toUpperCase();

                        return (
                            <Box
                                key={attendee._id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                }}
                            >
                                <Chip
                                    label={`${attendee.name || formatName(attendee.userId?.firstName, attendee.userId?.lastName)} (${contractTypeLabel})${validationDate ? ` - ${validationDate}` : ''}`}
                                    color={attendee.name ? 'default' : isValidated ? 'success' : 'warning'}
                                    size="small"
                                    icon={<Icon name={attendee.name ? 'personOutline' : 'person'} />}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {(dataDocuments?.data.length || 0) > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {dataDocuments?.data.map(
                        (doc) =>
                            doc.file?.url && (
                                <Box key={doc._id} sx={{ width: 120, height: 120, position: 'relative', cursor: 'pointer' }}>
                                    <img
                                        src={doc.file.url}
                                        alt={doc.file.fileName || 'photo'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                                        onClick={() => window.open(doc.file?.url, '_blank')}
                                    />
                                </Box>
                            )
                    )}
                </Box>
            )}

            <Box sx={{}}>
                {dataResponse &&
                    formTemplate.sections.map((section, sectionIndex) => (
                        <SectionResponseItem
                            key={section._id}
                            section={section}
                            sectionIndex={sectionIndex}
                            formColor={formTemplate.type?.color || '#000'}
                            response={dataResponse}
                        />
                    ))}
            </Box>
            {pdfViewerModal.value && dataResponse && (
                <PDFModal
                    open={pdfViewerModal.value}
                    handleClose={pdfViewerModal.setFalse}
                    fileName={`${formTemplate.title}_${formatName(dataResponse.filledBy.firstName, dataResponse.filledBy.lastName)}_${new Date(dataResponse.submittedAt || dataResponse.createdAt).toLocaleDateString('fr-FR').replace(/\//g, '-')}`}
                    PDFComponent={() => (
                        <FormResponsePDF
                            formTemplate={formTemplate}
                            formResponse={dataResponse}
                            documents={dataDocuments?.data}
                            attendees={dataAttendedUsers?.data}
                            t={t}
                        />
                    )}
                />
            )}
        </Box>
    );
};

export default FormResponse;
